<template>
  <div>
    <div v-if="isLoaded && survey">
      <TheDetailHeader
        :title="pageTitle"
        :parent="{ label: $t('pages.survey.surveyPage.title'), to: { name: 'SurveyList' } }"
      >
        <template #actions>
          <Button
            variant="outline"
            @click="
              $router.push({
                name: 'SurveyEdit',
                params: { surveyId: survey.id }
              })
            "
          >
            <Pencil />
            {{ $t('pages.survey.surveyPage.buttons.edit') }}
          </Button>
          <BaseConfirmButton
            v-if="!survey.isActive"
            :title="$t('pages.survey.surveyPage.confirm.activate.text')"
            :text="$t('pages.survey.surveyPage.confirm.activate.description')"
            :confirm-button-label="$t('global.words.confirm')"
            :cancel-button-label="$t('global.words.cancel')"
            @confirm="activate"
          >
            <CheckCircle />
            {{ $t('pages.survey.surveyPage.buttons.activate') }}
          </BaseConfirmButton>
          <BaseConfirmButton
            v-if="survey.isActive"
            :title="$t('pages.survey.surveyPage.confirm.disable.text')"
            :text="$t('pages.survey.surveyPage.confirm.disable.description')"
            :confirm-button-label="$t('global.words.confirm')"
            :cancel-button-label="$t('global.words.cancel')"
            @confirm="disable"
          >
            <Ban />
            {{ $t('pages.survey.surveyPage.buttons.disable') }}
          </BaseConfirmButton>
        </template>
      </TheDetailHeader>
      <div class="px-6 pb-6 sm:px-12 sm:pb-12">
        <div class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
          <AppCard title="pages.survey.surveyDetailPage.cards.recipients" :content="formatNumber(surveyedQuantity)" />
          <AppCard title="pages.survey.surveyDetailPage.cards.respondents" :content="formatNumber(answeredQuantity)" />
          <AppCard title="pages.survey.surveyDetailPage.cards.responseRate" :content="responseRate + '%'" />
        </div>

        <div class="mt-5 border sm:overflow-hidden sm:rounded-md">
          <div class="space-y-6 bg-white px-4 py-6 sm:p-6">
            <SurveyChart :chart-id="$route.params.surveyId" />
          </div>
        </div>

        <div class="mt-8">
          <div class="flex flex-col">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <AppTable
                  v-if="table.rows"
                  :columns="translatedColumns"
                  :rows="table.rows"
                  :pagination="table.pagination"
                  @update:change-page="changePage"
                >
                  <template #td-customer="{ data }">
                    <RowCustomer
                      :id="data.customer.id"
                      :country-flag="data.customer.countryFlag ?? null"
                      :has-optin="data.customer.hasOptin"
                      :name="data.customer.name"
                      :phone="data.customer.phone"
                    />
                  </template>
                  <template #td-date="{ data }">
                    <div>
                      <div class="text-sm font-medium text-foreground">{{ data.date.date }}</div>
                      <div v-if="data.date.time" class="text-sm text-muted-foreground">
                        {{ data.date.time }}
                      </div>
                    </div>
                  </template>
                </AppTable>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <TheLoadingPage v-else />
  </div>
</template>

<script>
import { Ban, CheckCircle, Pencil } from 'lucide-vue-next'
import AppCard from '@/components/ui/AppCard.vue'
import SurveyChart from './Components/SurveyChart.vue'
import TheLoadingPage from '@/components/layout/TheLoadingPage.vue'
import { BaseConfirmButton } from '@/ui/alert-dialog'
import { Button } from '@/ui/button'

import useNumber from '@/composables/number'

export default {
  components: {
    AppCard,
    Ban,
    CheckCircle,
    Pencil,
    SurveyChart,
    TheLoadingPage,
    Button,
    BaseConfirmButton
  },
  setup() {
    const notification = useNotification()
    const { formatNumber } = useNumber()

    return {
      notification,
      formatNumber
    }
  },
  data: () => {
    return {
      isLoaded: false,
      charts: [],
      pageTitle: '',
      surveyedQuantity: null,
      answeredQuantity: null,
      survey: null,
      table: {
        pagination: {
          currentPage: 1
        },
        columns: {
          customer: {
            label: 'pages.survey.surveyDetailPage.tables.thead.customer'
          },
          date: {
            label: 'pages.survey.surveyDetailPage.tables.thead.date'
          },
          answer: {
            label: 'pages.survey.surveyDetailPage.tables.thead.answer'
          }
        }
      }
    }
  },
  computed: {
    translatedColumns() {
      return Object.fromEntries(
        Object.entries(this.table.columns).map(([key, col]) => [key, { ...col, label: this.$t(col.label) }])
      )
    },
    responseRate() {
      return this.surveyedQuantity > 0
        ? Math.round((this.answeredQuantity / this.surveyedQuantity) * 100 * 100) / 100
        : 0
    },
    crumbs() {
      return [
        {
          name: this.$t('pages.survey.surveyPage.title'),
          route: 'SurveyList'
        },
        {
          name: this.$t('pages.survey.surveyDetailPage.title')
        }
      ]
    }
  },
  created() {
    this.axios.all([this.fetchData(), this.fetchTable()]).then((this.isLoaded = true))
  },
  methods: {
    closeConfirm() {
      this.confirmAction = null
    },
    activate() {
      this.axios
        .patch(`/surveys/${this.survey.id}`, {
          isActive: true
        })
        .then(() => {
          this.notification.create('success', {
            text: this.$t('pages.survey.surveyPage.confirm.activate.success')
          })
          this.survey.isActive = true
        })
    },
    disable() {
      this.axios
        .patch(`/surveys/${this.survey.id}`, {
          isActive: false
        })
        .then(() => {
          this.notification.create('success', {
            text: this.$t('pages.survey.surveyPage.confirm.disable.success')
          })
          this.survey.isActive = false
        })
    },
    changePage(page) {
      this.table.pagination.currentPage = page
      this.fetchTable()
    },
    fetchData() {
      this.axios.get(`/surveys/${this.$route.params.surveyId}`).then((response) => {
        this.survey = response.data.data
        this.pageTitle = this.survey.questionText
      })

      this.axios.get(`/surveys/${this.$route.params.surveyId}/stats`).then((response) => {
        this.surveyedQuantity = response.data.data.surveyedQuantity
        this.answeredQuantity = response.data.data.answeredQuantity
      })
    },
    fetchTable() {
      this.axios
        .get(`surveys/${this.$route.params.surveyId}/customers`, {
          params: {
            pageSize: 10,
            page: this.table.pagination.currentPage
          }
        })
        .then((response) => {
          var rows = []
          response.data.data.forEach((row) => {
            rows.push({
              id: row.customerMerchantId,
              data: {
                customer: {
                  id: row.customerMerchantId,
                  phone: row.customer,
                  name: row.customerFirstname,
                  gender: row.customerGender,
                  hasOptin: row.customerHasOptin,
                  countryFlag: row.countryFlag
                },
                date: {
                  date: new Date(row.date).toLocaleDateString('fr-FR'),
                  time: new Date(row.date).toLocaleTimeString('fr-FR')
                },
                answer: row.answer
              }
            })
          })
          this.table.rows = rows
          this.table.pagination = {
            currentPage: response.data.meta.current_page,
            lastPage: response.data.meta.last_page,
            total: response.data.meta.total
          }
        })
    }
  }
}
</script>
